<template>
  <main>
    <!-- ================= TABS ================= -->
    <div class="tabs-custom">
      <button
        class="tab-custom"
        :class="{ active: currentTab === 'list' }"
        @click="showTab('list')"
      >
        📋 Lista de Contactos
      </button>
      <button
        class="tab-custom"
        :class="{ active: currentTab === 'new' }"
        @click="showTab('new')"
      >
        ➕ Nuevo Contacto
      </button>
    </div>

    <!-- ================= VISTA LISTA DE CLIENTES ================= -->
    <section v-if="currentTab === 'list'" class="view">
      <!-- Filtros -->
      <div class="filters">
        <div class="filters-search">
          <label for="filterCliente">Buscar contacto</label>
          <input
            id="filterCliente"
            type="text"
            v-model.trim="filtroBusqueda"
            placeholder="Escribe el nombre del contacto..."
          />
        </div>
        <div class="filters-actions">
          <button class="btn-custom" @click="cargarClientes">
            Actualizar
          </button>
        </div>
      </div>

      <!-- KPIs / Stats -->
      <div class="dir-kpis">
        <div class="stat">
          <div class="n">{{ clientesFiltrados.length }}</div>
          <div class="l">Total de contactos</div>
        </div>
        <div class="stat" :class="{ warn: clientesConWarnings > 0 }">
          <div class="n">{{ clientesConWarnings }}</div>
          <div class="l">Con advertencias</div>
        </div>
        <div class="stat">
          <div class="n">{{ clientesConContactos }}</div>
          <div class="l">Con número telefónico</div>
        </div>
      </div>

      <!-- Grid de clientes -->
      <div class="contact-grid">
        <div
          v-for="cliente in clientesFiltrados"
          :key="idContactoDe(cliente)"
          class="contact-card"
          :class="{ warn: cliente.warnings }"
        >
          <div class="cc-head">
            <div class="cc-name">{{ cliente.nombre || "—" }}</div>
            <div class="cc-cuenta">
              {{ cliente.cuenta || "Cuenta" }} · ID {{ cliente.idCuenta }}
            </div>
          </div>

          <div
            v-if="cliente.puesto || cliente.email || cliente.telefono"
            class="cc-rows"
          >
            <div v-if="cliente.puesto" class="cc-row">
              <span class="k">Puesto</span>
              <span>{{ cliente.puesto }}</span>
            </div>

            <div v-if="cliente.email" class="cc-row">
              <span class="k">Email</span>
              <span>{{ cliente.email }}</span>
            </div>

            <div v-if="cliente.telefono" class="cc-row">
              <span class="k">Teléfono</span>
              <span>{{ cliente.telefono }}</span>
            </div>
          </div>

          <div
            v-if="
              cliente.nivelInfluencia ||
              cliente.evaluacionCualitativa ||
              cliente.warnings
            "
            class="cc-tags"
          >
            <span v-if="cliente.nivelInfluencia" class="tag-inf">{{
              cliente.nivelInfluencia
            }}</span>
            <span
              v-if="cliente.evaluacionCualitativa"
              class="eval-badge"
              :style="{
                color: getEvalColor(cliente.evaluacionCualitativa),
                borderColor: getEvalColor(cliente.evaluacionCualitativa) + '55',
              }"
            >
              {{ cliente.evaluacionCualitativa }}
            </span>
            <span v-if="cliente.warnings" class="tag-warn">
              ⚠️ Advertencia
            </span>
          </div>

          <div v-if="cliente.warnings" class="cc-warn">
            ⚠️ <span>{{ cliente.warnings }}</span>
          </div>

          <div class="cc-actions">
            <button class="btn-custom small" @click="editarCliente(cliente)">
              ✏️ Editar
            </button>
            <button
              class="btn-custom small danger"
              @click="eliminarCliente(cliente)"
            >
              🗑️ Eliminar
            </button>
          </div>
        </div>
      </div>

      <div v-if="!clientesFiltrados.length" class="empty">
        <p>No hay clientes registrados.</p>
        <button class="btn-custom" @click="showTab('new')">
          ➕ Agregar nuevo contacto
        </button>
      </div>
    </section>

    <!-- ================= VISTA NUEVO CLIENTE / EDICIÓN ================= -->
    <section v-if="currentTab === 'new'" class="view">
      <div class="view-head">
        <div>
          <div class="muted view-kicker">
            {{ isEditing ? "Editando contacto" : "Nuevo contacto" }}
          </div>
          <div class="section-main-title">
            {{ isEditing ? "Editar contacto" : "Agregar nuevo Contacto" }}
          </div>
        </div>
        <button class="btn-custom secondary" @click="cancelarEdicion">
          ← Cancelar
        </button>
      </div>

      <!-- Formulario -->
      <div class="card-custom">
        <div class="sec-title">
          <span class="bar-accent"></span>
          Información del contacto
        </div>
        <div class="sec-sub">
          Completa los datos del contacto para la cuenta
        </div>

        <div>
          <form class="form-grid" @submit.prevent="guardarCliente">
            <!-- Cuenta (selector desde /cuentas) -->
            <div class="form-group span-2">
              <label for="clienteIdCuenta">Cuenta *</label>

              <div v-if="cargandoCuentas" class="loading-row">
                <span class="spinner-cli"></span> Cargando cuentas…
              </div>

              <select
                v-else
                id="clienteIdCuenta"
                v-model.number="clienteForm.idCuenta"
                required
              >
                <option :value="null" disabled>Selecciona una cuenta…</option>
                <option
                  v-for="c in cuentas"
                  :key="c.idCuenta || c.idcuenta"
                  :value="c.idCuenta || c.idcuenta"
                >
                  {{ c.nombre }} (ID {{ c.idCuenta || c.idcuenta }})
                </option>
              </select>

              <div class="field-msg">
                Selecciona la cuenta del cliente
                <a
                  href="#"
                  style="margin-left: 6px"
                  @click.prevent="cargarCuentas"
                  >↻ recargar</a
                >
              </div>
            </div>

            <!-- Nombre -->
            <div class="form-group">
              <label for="clienteNombre">Nombre completo *</label>
              <input
                id="clienteNombre"
                type="text"
                v-model="clienteForm.nombre"
                placeholder="Ej: Aileen Hassey"
                required
              />
            </div>

            <!-- Puesto -->
            <div class="form-group">
              <label for="clientePuesto">Puesto</label>
              <input
                id="clientePuesto"
                type="text"
                v-model="clienteForm.puesto"
                placeholder="Ej: Coordinación de Logística"
              />
            </div>

            <!-- Email -->
            <div class="form-group">
              <label for="clienteEmail">Correo electrónico</label>
              <input
                id="clienteEmail"
                type="email"
                inputmode="email"
                autocomplete="email"
                v-model.trim="clienteForm.email"
                placeholder="Ej: aileen@cliente.com"
                :maxlength="EMAIL_MAX"
                :class="{ invalid: errorVisible('email') }"
                :aria-invalid="errorVisible('email') ? 'true' : 'false'"
                aria-describedby="clienteEmailMsg"
                @blur="touched.email = true"
              />
              <div
                id="clienteEmailMsg"
                class="field-msg"
                :class="{ error: errorVisible('email') }"
                aria-live="polite"
              >
                {{
                  errorVisible("email")
                    ? erroresForm.email
                    : "Opcional. Formato: nombre@empresa.com"
                }}
              </div>
            </div>

            <!-- Teléfono -->
            <div class="form-group">
              <label for="clienteTelefono">Teléfono</label>
              <input
                id="clienteTelefono"
                type="tel"
                inputmode="tel"
                autocomplete="tel"
                :value="clienteForm.telefono"
                placeholder="Ej: 55 2972 8720"
                :maxlength="TELEFONO_MAX"
                :class="{ invalid: errorVisible('telefono') }"
                :aria-invalid="errorVisible('telefono') ? 'true' : 'false'"
                aria-describedby="clienteTelefonoMsg"
                @input="onTelefonoInput"
                @blur="onTelefonoBlur"
              />
              <div
                id="clienteTelefonoMsg"
                class="field-msg"
                :class="{ error: errorVisible('telefono') }"
                aria-live="polite"
              >
                {{
                  errorVisible("telefono")
                    ? erroresForm.telefono
                    : "Opcional. 10 dígitos, o internacional con + (ej. +52 55 2972 8720)"
                }}
              </div>
            </div>

            <!-- Nivel de influencia -->
            <div class="form-group">
              <label for="clienteNivel">Nivel de influencia</label>
              <select
                id="clienteNivel"
                v-model="clienteForm.nivelInfluencia"
              >
                <option value="">Selecciona un nivel</option>
                <option value="DECISION MAKER">Decision Maker</option>
                <option value="INFLUENCIADOR">Influenciador</option>
                <option value="USER">User</option>
                <option value="GATEKEEPER">Gatekeeper</option>
              </select>
            </div>

            <!-- Evaluación cualitativa -->
            <div class="form-group">
              <label for="clienteEvaluacion">Evaluación cualitativa</label>
              <select
                id="clienteEvaluacion"
                v-model="clienteForm.evaluacionCualitativa"
              >
                <option value="">Selecciona una evaluación</option>
                <option value="PROMOTOR">Promotor</option>
                <option value="NEUTRO">Neutro</option>
                <option value="DETRACTOR">Detractor</option>
              </select>
            </div>

            <!-- Warnings / Notas -->
            <div class="form-group span-2">
              <label for="clienteWarnings">Advertencias / Notas</label>
              <textarea
                id="clienteWarnings"
                v-model="clienteForm.warnings"
                rows="3"
                placeholder="Información relevante sobre el cliente (opcional)"
              ></textarea>
              <div class="counter">
                {{ (clienteForm.warnings || "").length }}/1000 caracteres
              </div>
            </div>

            <!-- Botones -->
            <div class="form-actions span-2">
              <button
                type="button"
                class="btn-custom secondary"
                @click="cancelarEdicion"
              >
                Cancelar
              </button>
              <button type="submit" class="btn-custom" :disabled="!formValido">
                {{ isEditing ? "💾 Actualizar contacto" : "✅ Guardar contacto" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>

    <!-- TOAST -->
    <div class="toast-custom" :class="{ show: toastMessage }">
      {{ toastMessage }}
    </div>
  </main>
</template>

<script>
import { apiGet, API_BASE } from "@/services/api";
import {
  EMAIL_MAX,
  TELEFONO_MAX,
  validarEmail,
  validarTelefono,
  limpiarTelefono,
  formatearTelefono,
} from "@/utils/contactoValidators";

export default {
  name: "ClientesDashboard",
  data() {
    return {
      currentTab: "list",
      isEditing: false,

      // Lista de clientes
      clientes: [],
      clientesFiltrados: [],
      filtroBusqueda: "",

      // Cuentas (para el selector de "Cuenta" del formulario)
      cuentas: [],
      cargandoCuentas: false,

      // Formulario
      EMAIL_MAX,
      TELEFONO_MAX,
      touched: { email: false, telefono: false },
      clienteForm: {
        idContacto: null,
        idCuenta: null,
        nombre: "",
        puesto: "",
        email: "",
        telefono: "",
        nivelInfluencia: "",
        evaluacionCualitativa: "",
        warnings: "",
      },

      // Toast
      toastMessage: "",
      toastTimeout: null,

      // API base
      apiBase: `${API_BASE}/contactos`,
    };
  },

  computed: {
    clientesConWarnings() {
      return this.clientes.filter((c) => c.warnings).length;
    },

    clientesConContactos() {
      return this.clientes.filter((c) => c.email || c.telefono).length;
    },

    erroresForm() {
      const email = validarEmail(this.clienteForm.email);
      return {
        email: email || this.emailDuplicado(),
        telefono: validarTelefono(this.clienteForm.telefono),
      };
    },

    // Solo cuenta y nombre son obligatorios; el resto del contacto es opcional.
    formValido() {
      return Boolean(
        this.clienteForm.idCuenta && (this.clienteForm.nombre || "").trim(),
      );
    },
  },

  // ============ WATCH para detectar cambios en el buscador ============
  watch: {
    filtroBusqueda(newVal) {
      // Siempre ejecutar el filtro cuando cambie la búsqueda
      this.aplicarFiltros(newVal);
    },
  },

  mounted() {
    this.cargarClientes();
    this.cargarCuentas();
  },

  methods: {
    // ============ CUENTAS (selector) ============
    async cargarCuentas() {
      this.cargandoCuentas = true;
      try {
        const data = await apiGet("/cuentas");
        this.cuentas = data.items || data || [];
      } catch (error) {
        console.error("❌ Error al cargar cuentas:", error);
        this.mostrarToast("❌ Error al cargar las cuentas");
        this.cuentas = [];
      } finally {
        this.cargandoCuentas = false;
      }
    },

    // ============ NAVEGACIÓN ============
    showTab(tab) {
      this.currentTab = tab;
      if (tab === "list") {
        // Limpiar el filtro y recargar
        this.filtroBusqueda = "";
        this.cargarClientes();
      }
    },

    cancelarEdicion() {
      this.showTab("list");
      this.isEditing = false;
      this.resetForm();
    },

    // ============ VALIDACIÓN ============
    // El error se muestra tras salir del campo o al intentar guardar,
    // no mientras el usuario todavía está escribiendo.
    errorVisible(campo) {
      return this.touched[campo] && Boolean(this.erroresForm[campo]);
    },

    // CRM_CONTACTO tiene UQ(ID_CUENTA, EMAIL): se avisa antes de que ORDS lo rechace.
    emailDuplicado() {
      const email = (this.clienteForm.email || "").trim().toLowerCase();
      if (!email || !this.clienteForm.idCuenta) return "";
      const existe = this.clientes.some(
        (c) =>
          String(c.idCuenta || c.idcuenta) === String(this.clienteForm.idCuenta) &&
          (c.email || "").trim().toLowerCase() === email &&
          String(this.idContactoDe(c)) !== String(this.clienteForm.idContacto),
      );
      return existe ? "Ya existe un contacto con este correo en la cuenta." : "";
    },

    onTelefonoInput(e) {
      const limpio = limpiarTelefono(e.target.value);
      if (limpio !== e.target.value) e.target.value = limpio;
      this.clienteForm.telefono = limpio;
    },

    onTelefonoBlur() {
      this.touched.telefono = true;
      if (!this.erroresForm.telefono) {
        this.clienteForm.telefono = formatearTelefono(this.clienteForm.telefono);
      }
    },

    // ============ FILTRO ============
    aplicarFiltros(search) {
      // Si no hay clientes, salir
      if (!this.clientes || this.clientes.length === 0) {
        this.clientesFiltrados = [];
        return;
      }

      // Si no se pasa el valor, usar el de data
      const searchTerm = search !== undefined ? search : this.filtroBusqueda;
      const searchLower = (searchTerm || "").toLowerCase().trim();

      console.log("🔍 Aplicando filtro:", searchLower);

      if (!searchLower) {
        // Si no hay término de búsqueda, mostrar todos
        this.clientesFiltrados = [...this.clientes];
        console.log("📋 Mostrando todos:", this.clientesFiltrados.length);
        return;
      }

      // Filtrar clientes
      this.clientesFiltrados = this.clientes.filter((c) => {
        const id = String(c.idCuenta || c.idcuenta || "");
        const nombre = (c.nombre || "").toLowerCase();
        const puesto = (c.puesto || "").toLowerCase();
        const email = (c.email || "").toLowerCase();
        const telefono = (c.telefono || "").toLowerCase();

        const match =
          nombre.includes(searchLower) ||
          puesto.includes(searchLower) ||
          email.includes(searchLower) ||
          telefono.includes(searchLower) ||
          id.includes(searchLower);

        return match;
      });

      console.log("✅ Resultados:", this.clientesFiltrados.length);
    },

    // ============ CRUD CLIENTES ============
    async cargarClientes() {
      try {
        console.log("📥 Cargando clientes...");
        const response = await fetch(this.apiBase);
        if (!response.ok) throw new Error(`Error ${response.status}`);
        const data = await response.json();
        this.clientes = (data.items || data || []).map(this.normalizarContacto);

        console.log("📦 Clientes cargados:", this.clientes.length);

        // Aplicar el filtro actual (si existe)
        this.aplicarFiltros(this.filtroBusqueda);

        if (this.clientes.length > 0) {
          this.mostrarToast(`✅ ${this.clientes.length} clientes cargados`);
        }
      } catch (error) {
        console.error("❌ Error al cargar clientes:", error);
        this.mostrarToast("❌ Error al cargar los clientes");
        this.clientes = [];
        this.clientesFiltrados = [];
      }
    },

    async guardarCliente() {
      try {
        if (!this.clienteForm.idCuenta || this.clienteForm.idCuenta <= 0) {
          this.mostrarToast(
            "❌ El ID de cuenta debe ser un número válido mayor a 0",
          );
          return;
        }

        this.touched = { email: true, telefono: true };
        const campoInvalido = ["email", "telefono"].find(
          (c) => this.erroresForm[c],
        );
        if (campoInvalido) {
          this.mostrarToast(`❌ ${this.erroresForm[campoInvalido]}`);
          const id = campoInvalido === "email" ? "clienteEmail" : "clienteTelefono";
          this.$nextTick(() => document.getElementById(id)?.focus());
          return;
        }

        if (this.isEditing && !this.clienteForm.idContacto) {
          throw new Error("No se encontró el ID del contacto a actualizar");
        }

        const payload = {
          idCuenta: this.clienteForm.idCuenta,
          nombre: (this.clienteForm.nombre || "").trim(),
          puesto: (this.clienteForm.puesto || "").trim(),
          email: (this.clienteForm.email || "").trim(),
          telefono: (this.clienteForm.telefono || "").trim(),
          nivelInfluencia: this.clienteForm.nivelInfluencia,
          evaluacionCualitativa: this.clienteForm.evaluacionCualitativa,
          warnings: (this.clienteForm.warnings || "").trim(),
        };

        const method = this.isEditing ? "PUT" : "POST";
        const url = this.isEditing
          ? `${this.apiBase}/${this.clienteForm.idContacto}`
          : this.apiBase;

        const response = await fetch(url, {
          method: method,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        await this.leerRespuesta(response);

        this.mostrarToast(
          this.isEditing
            ? "✅ Cliente actualizado correctamente"
            : "✅ Cliente creado correctamente",
        );
        this.resetForm();
        this.isEditing = false;
        await this.cargarClientes();
        this.showTab("list");
      } catch (error) {
        console.error("❌ Error al guardar cliente:", error);
        this.mostrarToast(
          `❌ ${this.mensajeError(error, "Error al guardar el cliente")}`,
        );
      }
    },

    editarCliente(cliente) {
      this.isEditing = true;
      this.touched = { email: false, telefono: false };
      this.clienteForm = {
        idContacto: this.idContactoDe(cliente),
        idCuenta: cliente.idCuenta || cliente.idcuenta,
        nombre: cliente.nombre || "",
        puesto: cliente.puesto || "",
        email: cliente.email || "",
        telefono: cliente.telefono || "",
        nivelInfluencia: cliente.nivelInfluencia || "",
        evaluacionCualitativa: cliente.evaluacionCualitativa || "",
        warnings: cliente.warnings || "",
      };
      this.showTab("new");
    },

    async eliminarCliente(cliente) {
      if (!confirm(`¿Estás seguro de eliminar a "${cliente.nombre}"?`)) return;

      try {
        const id = this.idContactoDe(cliente);
        if (!id) throw new Error("No se encontró el ID del contacto");

        const response = await fetch(`${this.apiBase}/${id}`, {
          method: "DELETE",
          headers: { Accept: "application/json" },
        });

        await this.leerRespuesta(response);

        this.mostrarToast(`🗑️ Cliente "${cliente.nombre}" eliminado`);
        await this.cargarClientes();
      } catch (error) {
        console.error("❌ Error al eliminar cliente:", error);
        this.mostrarToast(
          `❌ ${this.mensajeError(error, "Error al eliminar el cliente")}`,
        );
      }
    },

    resetForm() {
      this.touched = { email: false, telefono: false };
      this.clienteForm = {
        idContacto: null,
        idCuenta: null,
        nombre: "",
        puesto: "",
        email: "",
        telefono: "",
        nivelInfluencia: "",
        evaluacionCualitativa: "",
        warnings: "",
      };
    },

    // ============ UTILIDADES ============
    // PK del contacto. ORDS puede devolver la llave en camelCase o minúsculas.
    idContactoDe(cliente) {
      return cliente.idContacto ?? cliente.idcontacto ?? cliente.id_contacto ?? null;
    },

    // fetch() lanza TypeError cuando el navegador bloquea la petición (CORS o
    // red): ORDS no expone el recurso y el preflight vuelve sin cabeceras CORS.
    mensajeError(error, porDefecto) {
      if (error instanceof TypeError) {
        return "No se pudo contactar al servidor (CORS/red). Verifica que el endpoint exista en ORDS";
      }
      return error.message || porDefecto;
    },

    // El GET de ORDS (collection_feed) devuelve las columnas en minúsculas
    // (idcontacto, nivelinfluencia...); la vista trabaja en camelCase.
    normalizarContacto(c) {
      return {
        ...c,
        idContacto: this.idContactoDe(c),
        idCuenta: c.idCuenta ?? c.idcuenta ?? null,
        nivelInfluencia: c.nivelInfluencia ?? c.nivelinfluencia ?? null,
        evaluacionCualitativa:
          c.evaluacionCualitativa ?? c.evaluacioncualitativa ?? null,
      };
    },

    /**
     * Lee la respuesta de ORDS sin asumir cuerpo JSON: PUT/DELETE suelen
     * responder 204 o vacío, y response.json() lanzaría "Unexpected end of JSON input".
     * Los handlers PL/SQL responden HTTP 200 con { ERROR_ID, MENSAJE }, así que
     * un ERROR_ID distinto de 200 también es un error.
     */
    async leerRespuesta(response) {
      const texto = await response.text();
      let data = null;
      try {
        data = texto ? JSON.parse(texto) : null;
      } catch (e) {
        data = null;
      }
      const mensaje = data && (data.MENSAJE || data.mensaje || data.message);
      if (!response.ok) {
        throw new Error(
          response.status === 404 || response.status === 405
            ? "El servidor no tiene habilitada esta operación (falta el endpoint en ORDS)"
            : mensaje || `Error ${response.status}`,
        );
      }
      const errorId = data && Number(data.ERROR_ID);
      if (errorId && errorId !== 200) {
        throw new Error(mensaje || `Error ${errorId}`);
      }
      return data;
    },

    getEvalColor(evalType) {
      const colors = {
        PROMOTOR: "#2ECC71",
        NEUTRO: "#F1C40F",
        DETRACTOR: "#E74C3C",
      };
      return colors[evalType] || "#95A5A6";
    },

    mostrarToast(mensaje) {
      this.toastMessage = mensaje;
      clearTimeout(this.toastTimeout);
      this.toastTimeout = setTimeout(() => {
        this.toastMessage = "";
      }, 4000);
    },
    
  },
};
</script>
<style scoped>
/* ============================================
   VARIABLES Y RESET
   ============================================ */
:root {
  --primary: #003ca5;
  --primary-dark: #002a7a;
  --primary-light: #1a5cc9;
  --secondary: #43b234;
  --secondary-light: #5cc94e;
  --white: #ffffff;
  --gray-100: #f0f4fa;
  --gray-200: #d4dff0;
  --gray-300: #a0b8d8;
  --ink: #0a1628;
  --ink2: #1a2a4a;
  --surface: rgba(0, 0, 0, 0.04);
  --surface-2: rgba(0, 0, 0, 0.06);
  --border: rgba(0, 0, 0, 0.1);
  --border-2: rgba(0, 0, 0, 0.16);
  --txt: #0a1628;
  --txt-dim: #4a5a7a;
  --txt-faint: #8090b0;
  --good: #43b234;
  --lime: #5cc94e;
  --warn: #ffc24b;
  --bad: #ff5d73;
  --accent: #003ca5;
  --accent2: #1a5cc9;
  --font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial,
    sans-serif;
  --mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.08);
  --shadow-md: 0 8px 32px rgba(0, 0, 0, 0.12);
  --shadow-lg: 0 16px 48px rgba(0, 0, 0, 0.18);
}

/* ============================================
   ESTILOS GENERALES
   ============================================ */
* {
  box-sizing: border-box;
}

/* Escala de espaciado de la vista (múltiplos de 4px) */
main {
  --sp-xs: 8px;
  --sp-sm: 12px;
  --sp-md: 16px;
  --sp-lg: 24px;

  position: relative;
  z-index: 1;
  max-width: 1400px; /* mismo ancho que DashboardGeneral (.dg) */
  margin: 0 auto;
  padding: clamp(16px, 3vw, 32px);
  display: flex;
  flex-direction: column;
  gap: var(--sp-lg);
}

/* Cada vista apila sus bloques con el mismo ritmo vertical */
.view {
  display: flex;
  flex-direction: column;
  gap: var(--sp-lg);
}

.view-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--sp-md);
  flex-wrap: wrap;
}

.view-kicker {
  font-size: 12px;
  margin-bottom: 4px;
}

/* ============================================
   TABS
   ============================================ */
.tabs-custom {
  display: flex;
  gap: 4px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 4px;
  width: fit-content;
  flex-wrap: wrap;
}

.tab-custom {
  font: inherit;
  font-weight: 600;
  font-size: clamp(12px, 1.1vw, 14px);
  color: var(--txt-dim);
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px 20px;
  border-radius: 10px;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.tab-custom:hover {
  color: var(--txt);
  background: var(--surface-2);
}

.tab-custom.active {
  color: var(--on-accent);
  background: linear-gradient(135deg, var(--secondary), var(--primary));
  box-shadow: 0 4px 16px rgba(67, 178, 52, 0.25);
}

/* ============================================
   ROW / GRID
   ============================================ */
.row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
}

.row > * {
  flex: 1 1 auto;
  min-width: 180px;
}

.grid {
  display: grid;
  gap: 16px;
}

/* ============================================
   LABELS Y INPUTS
   ============================================ */
label {
  display: block;
  font-size: 11px;
  color: var(--txt-dim);
  margin-bottom: var(--sp-xs);
  font-weight: 600;
  letter-spacing: 0.05em;
}

select,
input,
textarea {
  font: inherit;
  padding: 10px 14px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--white);
  color: var(--txt);
  width: 100%;
  outline: none;
  transition: all 0.3s ease;
}

select {
  min-width: 200px;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%234A5A7A' stroke-width='1.5' fill='none'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 40px;
}

select option {
  background: var(--white);
  color: var(--txt);
}

select:focus,
input:focus,
textarea:focus {
  border-color: var(--secondary);
  box-shadow: 0 0 0 3px rgba(67, 178, 52, 0.15);
}

/* ============================================
   BOTONES
   El texto usa --on-accent (siempre blanco): --white se redefine
   como color de superficie en modo oscuro.
   ============================================ */
.btn-custom {
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  color: var(--on-accent);
  border: none;
  border-radius: 12px;
  padding: 11px 24px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 60, 165, 0.25);
  letter-spacing: 0.02em;
  font-size: 14px;
  white-space: nowrap;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-custom:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(0, 60, 165, 0.35);
  color: var(--on-accent);
}

.btn-custom:active {
  transform: translateY(0);
}

.btn-custom.secondary {
  background: var(--surface);
  color: var(--txt);
  border: 1px solid var(--border);
  box-shadow: none;
}

.btn-custom.secondary:hover {
  background: var(--surface-2);
  border-color: var(--border-2);
  color: var(--txt);
}

.btn-custom.danger {
  background: linear-gradient(135deg, #f06c5e, #f06c5e);
  box-shadow: 0 4px 16px rgba(231, 76, 60, 0.25);
}

.btn-custom.danger:hover {
  box-shadow: 0 8px 28px rgba(231, 76, 60, 0.35);
}

.btn-custom.small {
  padding: 6px 14px;
  font-size: 12px;
  min-height: 32px;
}

.btn-custom:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

/* ============================================
   CARDS
   ============================================ */
.card-custom {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: clamp(20px, 3vw, 32px);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-sm);
}

.card-custom:hover {
  border-color: var(--border-2);
  box-shadow: var(--shadow-md);
}

.label-custom {
  font-size: 10px;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--txt-faint);
  font-weight: 700;
  margin-bottom: 4px;
}

.sec-title {
  font-size: clamp(16px, 2vw, 20px);
  font-weight: 700;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.sec-title .bar-accent {
  width: 4px;
  height: 24px;
  border-radius: 4px;
  background: linear-gradient(180deg, var(--secondary), var(--primary));
  flex-shrink: 0;
}

.sec-sub {
  font-size: clamp(12px, 1.2vw, 14px);
  color: var(--txt-dim);
  margin-bottom: var(--sp-lg);
  margin-left: 14px; /* alinea con el título, después de la barra de acento */
  padding-bottom: var(--sp-lg);
  border-bottom: 1px solid var(--border);
}

.muted {
  color: var(--txt-dim);
}

.section-main-title {
  font-size: clamp(18px, 2.5vw, 24px);
  font-weight: 700;
}

.counter {
  font-size: 11px;
  color: var(--txt-faint);
  text-align: right;
  margin-top: 4px;
}

/* Cargando cuentas */
.loading-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--white);
  color: var(--txt-dim);
  font-size: 14px;
}

.spinner-cli {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(0, 60, 165, 0.25);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin-cli 0.6s linear infinite;
}

@keyframes spin-cli {
  to {
    transform: rotate(360deg);
  }
}

/* ============================================
   FILTROS
   ============================================ */
.filters {
  display: flex;
  gap: var(--sp-md);
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: clamp(16px, 2vw, 24px);
  box-shadow: var(--shadow-sm);
}

.filters-search {
  flex: 1 1 320px;
  max-width: 480px;
}

.filters-actions {
  display: flex;
  gap: var(--sp-xs);
}

/* ============================================
   STATS / KPIS
   ============================================ */
.dir-kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--sp-md);
}

.stat {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: clamp(16px, 2vw, 24px);
  transition: all 0.3s ease;
  box-shadow: var(--shadow-sm);
}

.stat .n {
  font-family: var(--mono);
  font-size: clamp(28px, 3vw, 36px);
  font-weight: 700;
  line-height: 1.2;
}

.stat .l {
  font-size: clamp(10px, 1vw, 12px);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--txt-faint);
  margin-top: var(--sp-xs);
  font-weight: 600;
}

.stat.warn {
  background: linear-gradient(120deg, rgba(255, 194, 75, 0.06), var(--white));
  border-color: rgba(255, 194, 75, 0.2);
}

.stat.warn .n {
  color: var(--warn);
}

/* ============================================
   GRID DE CLIENTES
   ============================================ */
.contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: clamp(16px, 2vw, 24px);
}

.contact-card {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: clamp(16px, 2vw, 24px);
  transition: all 0.3s ease;
  position: relative;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: var(--sp-md);
}

.cc-head {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.contact-card:hover {
  border-color: var(--border-2);
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
}

.contact-card.warn {
  border-color: rgba(255, 194, 75, 0.3);
  background: linear-gradient(120deg, rgba(255, 194, 75, 0.04), var(--white));
}

.cc-name {
  font-size: clamp(15px, 1.3vw, 18px);
  font-weight: 700;
  color: var(--txt);
}

.cc-puesto {
  font-size: clamp(12px, 1vw, 14px);
  color: var(--txt-dim);
  margin-top: 2px;
}

.cc-cuenta {
  font-size: clamp(11px, 1vw, 13px);
  color: var(--secondary);
  font-weight: 600;
}

.cc-rows {
  display: flex;
  flex-direction: column;
}

.cc-row {
  display: flex;
  gap: var(--sp-sm);
  font-size: clamp(12px, 1vw, 14px);
  color: var(--txt-dim);
  word-break: break-word;
  padding: var(--sp-xs) 0;
  border-bottom: 1px solid var(--border);
}

.cc-row:first-child {
  padding-top: 0;
}

.cc-row:last-child {
  padding-bottom: 0;
  border-bottom: none;
}

.cc-row .k {
  color: var(--txt-faint);
  min-width: 68px;
  flex-shrink: 0;
}

.cc-tags {
  display: flex;
  gap: var(--sp-xs);
  flex-wrap: wrap;
}

.tag-inf {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--txt-dim);
}

.eval-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid var(--border-2);
}

.tag-warn {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(255, 194, 75, 0.12);
  border: 1px solid rgba(255, 194, 75, 0.2);
  color: #b7950b;
}

.cc-warn {
  display: flex;
  gap: var(--sp-xs);
  align-items: flex-start;
  font-size: clamp(12px, 1vw, 14px);
  color: #7d6608;
  background: rgba(255, 194, 75, 0.06);
  border: 1px solid rgba(255, 194, 75, 0.15);
  border-radius: 10px;
  padding: var(--sp-sm) var(--sp-md);
}

/* margin-top: auto ancla las acciones al pie: alinea las tarjetas de una fila */
.cc-actions {
  display: flex;
  gap: var(--sp-xs);
  margin-top: auto;
  border-top: 1px solid var(--border);
  padding-top: var(--sp-md);
}

.cc-actions .btn-custom {
  flex: 1;
  justify-content: center;
  min-height: 40px;
}

/* ============================================
   FORMULARIO
   ============================================ */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--sp-lg);
}

.span-2 {
  grid-column: 1 / -1;
}

.form-group {
  min-width: 0;
}

.form-group label {
  font-size: 12px;
  font-weight: 600;
  color: var(--txt);
  letter-spacing: 0.03em;
}

.form-group label:after {
  content: " *";
  color: var(--bad);
}

.form-group:has(textarea) label:after {
  content: "";
}

.form-group textarea {
  min-height: 80px;
  resize: vertical;
}

.form-group input.invalid {
  border-color: var(--bad);
  box-shadow: 0 0 0 3px rgba(255, 93, 115, 0.15);
}

.field-msg {
  font-size: 11px;
  color: var(--txt-faint);
  margin-top: 6px;
  min-height: 16px;
}

.field-msg.error {
  color: var(--bad);
  font-weight: 600;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: var(--sp-xs);
  border-top: 1px solid var(--border);
  padding-top: var(--sp-lg);
}

/* ============================================
   EMPTY STATE
   ============================================ */
.empty {
  background: var(--white);
  border: 1px dashed var(--border-2);
  border-radius: 16px;
  padding: clamp(32px, 5vw, 48px);
  text-align: center;
  color: var(--txt-dim);
}

.empty b {
  color: var(--txt);
}

.empty .btn-custom {
  margin-top: var(--sp-md);
}

/* ============================================
   TOAST
   ============================================ */
.toast-custom {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(20px);
  background: var(--ink2);
  color: var(--white);
  border: 1px solid var(--border-2);
  padding: 14px 24px;
  border-radius: 14px;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  pointer-events: none;
  z-index: 100;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: var(--shadow-lg);
  font-size: clamp(13px, 1.1vw, 15px);
  max-width: 90vw;
  text-align: center;
}

.toast-custom.show {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* ============================================
   ANIMACIONES
   ============================================ */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(67, 178, 52, 0.5);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(67, 178, 52, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(67, 178, 52, 0);
  }
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 1024px) {
  .dir-kpis {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .dir-kpis {
    grid-template-columns: repeat(2, 1fr);
  }

  .contact-grid {
    grid-template-columns: 1fr;
  }

  .form-grid {
    grid-template-columns: 1fr;
    gap: var(--sp-md);
  }

  main,
  .view {
    gap: var(--sp-md);
  }

  .tabs-custom {
    width: 100%;
  }

  .tab-custom {
    flex: 1;
    text-align: center;
    padding: 10px 12px;
    font-size: 12px;
  }
}

@media (max-width: 680px) {
  .row {
    flex-direction: column;
    align-items: stretch;
  }

  .row > * {
    min-width: unset;
  }

  .filters {
    flex-direction: column;
    align-items: stretch;
  }

  .filters > div {
    min-width: unset;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-custom {
    width: 100%;
    justify-content: center;
  }

  .filters-search {
    max-width: none;
  }
}

@media (max-width: 480px) {
  .dir-kpis {
    grid-template-columns: 1fr;
  }

  .stat {
    text-align: center;
  }

  .contact-card {
    padding: var(--sp-md);
  }

  .tabs-custom {
    border-radius: 10px;
    padding: 3px;
  }

  .tab-custom {
    padding: 8px 10px;
    font-size: 11px;
    border-radius: 8px;
  }
}

@media (max-width: 360px) {
  .tab-custom {
    font-size: 10px;
    padding: 6px 8px;
  }
}

/* ============================================
   SCROLLBAR
   ============================================ */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: var(--primary);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--primary-light);
}
</style>
